import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { BookingModeDto } from '@/api/generated';

dayjs.extend(utc);
dayjs.extend(timezone);

export const BUSINESS_TIME_ZONE = 'Europe/Warsaw';
export const DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'HH:mm';
export const DISPLAY_DATE_FORMAT = 'DD.MM.YYYY';
export const DISPLAY_DATE_TIME_FORMAT = `${DISPLAY_DATE_FORMAT}, ${TIME_FORMAT}`;

/** A full hour on the clock, e.g. `09:00`. */
export const formatHour = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

/** A day with a full hour on the clock, e.g. `05.03.2026, 09:00`. */
export const formatDisplayDateHour = (date: Dayjs, hour: number): string =>
  `${date.format(DISPLAY_DATE_FORMAT)}, ${formatHour(hour)}`;

export const businessDay = (value: Dayjs): Dayjs =>
  dayjs.tz(value.format(DATE_FORMAT), BUSINESS_TIME_ZONE);

export const nextBusinessDay = (value: Dayjs): Dayjs =>
  dayjs.tz(
    dayjs.utc(value.format(DATE_FORMAT)).add(1, 'day').format(DATE_FORMAT),
    BUSINESS_TIME_ZONE,
  );

/**
 * A full hour of a business day on the wall clock, with an exclusive end: empty for the hour skipped
 * by the spring DST change, two real hours for the hour repeated in autumn.
 */
export const businessHourRange = (value: Dayjs, hour: number): { from: Dayjs; to: Dayjs } => {
  const date = value.format(DATE_FORMAT);
  return {
    from: dayjs.tz(`${date} ${formatHour(hour)}`, BUSINESS_TIME_ZONE),
    to:
      hour === 23
        ? nextBusinessDay(value)
        : dayjs.tz(`${date} ${formatHour(hour + 1)}`, BUSINESS_TIME_ZONE),
  };
};

export type ReservationRange = {
  from: Dayjs | null;
  to: Dayjs | null;
};

const isFullDayMode = (bookingMode: BookingModeDto): boolean =>
  bookingMode === BookingModeDto.FullDay;

/** The start of a form range as the request carries it: a full-day start is the start of its day. */
export const reservationStartToRequest = (bookingMode: BookingModeDto, from: Dayjs): Dayjs =>
  isFullDayMode(bookingMode) ? businessDay(from) : from;

/**
 * The end of a form range as the request carries it: a full-day end (the last day) becomes exclusive,
 * the start of the next business day.
 */
export const reservationEndToRequest = (bookingMode: BookingModeDto, to: Dayjs): Dayjs =>
  isFullDayMode(bookingMode) ? nextBusinessDay(to) : to;

/** The start of a saved range as the form holds it: the business day or the exact time. */
export const reservationStartFromDto = (bookingMode: BookingModeDto, value: string): Dayjs =>
  isFullDayMode(bookingMode)
    ? businessDay(dayjs(value).tz(BUSINESS_TIME_ZONE))
    : dayjs(value).tz(BUSINESS_TIME_ZONE);

/** The end of a saved range as the form holds it: in a full-day mode the last day it takes. */
export const reservationEndFromDto = (bookingMode: BookingModeDto, value: string): Dayjs =>
  isFullDayMode(bookingMode)
    ? businessDay(dayjs(value).subtract(1, 'millisecond').tz(BUSINESS_TIME_ZONE))
    : dayjs(value).tz(BUSINESS_TIME_ZONE);

/** A time of a range in the short text of a read view: the day in a full-day mode, otherwise with the hour. */
export const formatReservationTime = (bookingMode: BookingModeDto, value: Dayjs): string =>
  value
    .tz(BUSINESS_TIME_ZONE)
    .format(isFullDayMode(bookingMode) ? DISPLAY_DATE_FORMAT : DISPLAY_DATE_TIME_FORMAT);

/**
 * Form range to request range: a full-day end becomes exclusive (start of the next business day),
 * an exact-time range stays as entered.
 */
export const reservationRangeToRequest = (
  bookingMode: BookingModeDto,
  from: Dayjs,
  to: Dayjs,
): { from: Dayjs; to: Dayjs } => ({
  from: reservationStartToRequest(bookingMode, from),
  to: reservationEndToRequest(bookingMode, to),
});

export const reservationRangeFromDto = (
  bookingMode: BookingModeDto,
  reservationFrom?: string,
  reservationTo?: string,
): ReservationRange => ({
  from: reservationFrom ? reservationStartFromDto(bookingMode, reservationFrom) : null,
  to: reservationTo ? reservationEndFromDto(bookingMode, reservationTo) : null,
});

export const formatReservationRange = (
  bookingMode: BookingModeDto,
  reservationFrom: string,
  reservationTo: string,
): { from: string; to: string } => {
  const range = reservationRangeFromDto(bookingMode, reservationFrom, reservationTo);

  return {
    from: formatReservationTime(bookingMode, range.from!),
    to: formatReservationTime(bookingMode, range.to!),
  };
};
